/* eslint-disable react/jsx-no-bind */
import classNames from 'classnames';
import PropTypes from 'prop-types';
import React from 'react';
import {defineMessages, useIntl} from 'react-intl';

import Modal from '../../containers/modal.jsx';

import styles from './domi-controller-simulator.css';

const messages = defineMessages({
    title: {
        id: 'gui.domiControllerSimulator.title',
        defaultMessage: 'Controller Simulator',
        description: 'Title of the Domi controller simulator'
    },
    introduction: {
        id: 'gui.domiControllerSimulator.introduction',
        defaultMessage: 'Hold a button to simulate the physical controller. Release it to trigger the release event.',
        description: 'Introduction shown in the Domi controller simulator'
    },
    connected: {
        id: 'gui.domiControllerSimulator.connected',
        defaultMessage: 'The car is connected over Wi-Fi and is ready for live testing.',
        description: 'Connected status in the Domi controller simulator'
    },
    disconnected: {
        id: 'gui.domiControllerSimulator.disconnected',
        defaultMessage: 'The car is not connected. Events still run, but car blocks cannot control the physical car.',
        description: 'Disconnected status in the Domi controller simulator'
    },
    recentEvents: {
        id: 'gui.domiControllerSimulator.recentEvents',
        defaultMessage: 'Recent events',
        description: 'Heading above recent simulated controller events'
    },
    noEvents: {
        id: 'gui.domiControllerSimulator.noEvents',
        defaultMessage: 'Press a controller button to start testing.',
        description: 'Empty event history in the Domi controller simulator'
    },
    eventItem: {
        id: 'gui.domiControllerSimulator.eventItem',
        defaultMessage: '{button} {action}',
        description: 'A simulated controller event in the event history'
    },
    pressed: {
        id: 'gui.domiControllerSimulator.pressed',
        defaultMessage: 'pressed',
        description: 'Pressed action in the Domi controller simulator'
    },
    released: {
        id: 'gui.domiControllerSimulator.released',
        defaultMessage: 'released',
        description: 'Released action in the Domi controller simulator'
    },
    emergencyStop: {
        id: 'gui.domiControllerSimulator.emergencyStop',
        defaultMessage: 'Stop the car now',
        description: 'Emergency stop button in the Domi controller simulator'
    },
    safetyHelp: {
        id: 'gui.domiControllerSimulator.safetyHelp',
        defaultMessage: 'Closing this window or switching away stops the car automatically.',
        description: 'Safety help in the Domi controller simulator'
    },
    up: {
        id: 'gui.domiControllerSimulator.up',
        defaultMessage: 'Up',
        description: 'Up button label in the Domi controller simulator'
    },
    down: {
        id: 'gui.domiControllerSimulator.down',
        defaultMessage: 'Down',
        description: 'Down button label in the Domi controller simulator'
    },
    left: {
        id: 'gui.domiControllerSimulator.left',
        defaultMessage: 'Left',
        description: 'Left button label in the Domi controller simulator'
    },
    right: {
        id: 'gui.domiControllerSimulator.right',
        defaultMessage: 'Right',
        description: 'Right button label in the Domi controller simulator'
    }
});

const buttonMessages = {
    UP: messages.up,
    DOWN: messages.down,
    LEFT: messages.left,
    RIGHT: messages.right
};

const ControllerButton = ({active, button, children, className, label, onPress, onRelease}) => {
    const handlePress = event => {
        event.preventDefault();
        onPress(button);
    };
    const handleRelease = event => {
        event.preventDefault();
        onRelease(button);
    };
    const handleKeyDown = event => {
        if (event.key !== 'Enter' && event.key !== ' ') return;
        handlePress(event);
    };
    const handleKeyUp = event => {
        if (event.key !== 'Enter' && event.key !== ' ') return;
        handleRelease(event);
    };

    return (
        <button
            aria-label={label}
            aria-pressed={active}
            className={classNames(styles.controllerButton, className, {
                [styles.controllerButtonActive]: active
            })}
            data-controller-button={button}
            type="button"
            onBlur={handleRelease}
            onContextMenu={event => event.preventDefault()}
            onKeyDown={handleKeyDown}
            onKeyUp={handleKeyUp}
            onPointerCancel={handleRelease}
            onPointerDown={handlePress}
            onPointerLeave={handleRelease}
            onPointerUp={handleRelease}
        >
            {children}
        </button>
    );
};

ControllerButton.propTypes = {
    active: PropTypes.bool,
    button: PropTypes.string.isRequired,
    children: PropTypes.node.isRequired,
    className: PropTypes.string,
    label: PropTypes.string.isRequired,
    onPress: PropTypes.func.isRequired,
    onRelease: PropTypes.func.isRequired
};

const DomiControllerSimulator = ({
    activeButtons,
    connected,
    eventHistory,
    isOpen,
    onButtonPress,
    onButtonRelease,
    onClose,
    onEmergencyStop
}) => {
    const intl = useIntl();
    if (!isOpen) return null;

    const localizedButtonName = button => {
        if (buttonMessages[button]) return intl.formatMessage(buttonMessages[button]);
        return button;
    };
    const localizedActionName = action => intl.formatMessage(
        action === 'PRESS' ? messages.pressed : messages.released
    );
    const isActive = button => activeButtons.includes(button);
    const controllerButtonProps = (button, label) => ({
        active: isActive(button),
        button,
        label,
        onPress: onButtonPress,
        onRelease: onButtonRelease
    });

    return (
        <Modal
            className={styles.modal}
            contentLabel={intl.formatMessage(messages.title)}
            id="domiControllerSimulator"
            onRequestClose={onClose}
        >
            <div className={styles.content}>
                <p className={styles.introduction}>{intl.formatMessage(messages.introduction)}</p>
                <div
                    className={classNames(styles.connectionStatus, {
                        [styles.connected]: connected,
                        [styles.disconnected]: !connected
                    })}
                >
                    <span className={styles.statusDot} />
                    {intl.formatMessage(connected ? messages.connected : messages.disconnected)}
                </div>

                <div className={styles.workspace}>
                    <div className={styles.controllerShell}>
                        <div className={styles.dpad}>
                            <ControllerButton
                                {...controllerButtonProps('UP', intl.formatMessage(messages.up))}
                                className={styles.dpadUp}
                            >▲</ControllerButton>
                            <ControllerButton
                                {...controllerButtonProps('LEFT', intl.formatMessage(messages.left))}
                                className={styles.dpadLeft}
                            >◀</ControllerButton>
                            <ControllerButton
                                {...controllerButtonProps('OK', 'OK')}
                                className={styles.dpadOk}
                            >OK</ControllerButton>
                            <ControllerButton
                                {...controllerButtonProps('RIGHT', intl.formatMessage(messages.right))}
                                className={styles.dpadRight}
                            >▶</ControllerButton>
                            <ControllerButton
                                {...controllerButtonProps('DOWN', intl.formatMessage(messages.down))}
                                className={styles.dpadDown}
                            >▼</ControllerButton>
                        </div>
                        <div className={styles.functionKeys}>
                            {['A', 'B', 'C', 'D', 'E', 'F'].map(button => (
                                <ControllerButton
                                    {...controllerButtonProps(button, button)}
                                    key={button}
                                    className={styles.functionKey}
                                >{button}</ControllerButton>
                            ))}
                        </div>
                    </div>

                    <div className={styles.eventPanel}>
                        <h3>{intl.formatMessage(messages.recentEvents)}</h3>
                        {eventHistory.length ? (
                            <ol className={styles.eventList}>
                                {eventHistory.map(event => (
                                    <li key={event.id}>
                                        <span>{intl.formatMessage(messages.eventItem, {
                                            action: localizedActionName(event.action),
                                            button: localizedButtonName(event.button)
                                        })}</span>
                                        <code>{event.button} {event.action}</code>
                                    </li>
                                ))}
                            </ol>
                        ) : (
                            <p className={styles.emptyEvents}>{intl.formatMessage(messages.noEvents)}</p>
                        )}
                    </div>
                </div>

                <div className={styles.footer}>
                    <span>{intl.formatMessage(messages.safetyHelp)}</span>
                    <button
                        className={styles.emergencyStop}
                        type="button"
                        onClick={onEmergencyStop}
                    >
                        {intl.formatMessage(messages.emergencyStop)}
                    </button>
                </div>
            </div>
        </Modal>
    );
};

DomiControllerSimulator.propTypes = {
    activeButtons: PropTypes.arrayOf(PropTypes.string),
    connected: PropTypes.bool,
    eventHistory: PropTypes.arrayOf(PropTypes.shape({
        action: PropTypes.string.isRequired,
        button: PropTypes.string.isRequired,
        id: PropTypes.number.isRequired
    })),
    isOpen: PropTypes.bool,
    onButtonPress: PropTypes.func.isRequired,
    onButtonRelease: PropTypes.func.isRequired,
    onClose: PropTypes.func.isRequired,
    onEmergencyStop: PropTypes.func.isRequired
};

DomiControllerSimulator.defaultProps = {
    activeButtons: [],
    connected: false,
    eventHistory: [],
    isOpen: false
};

export default DomiControllerSimulator;
